import { z } from "zod";

export const companyFileKinds = ["logo", "document"] as const;
export const companyFileKindSchema = z.enum(companyFileKinds);

const filenameSchema = z.string().trim().min(1).max(255);
const mimeTypeSchema = z.string().trim().min(1).max(160);
const storageKeySchema = z.string().trim().min(1).max(1024);
const checksumSha256Schema = z.string().regex(/^[a-f0-9]{64}$/);

export const companyFileSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  kind: companyFileKindSchema,
  originalFilename: filenameSchema,
  mimeType: mimeTypeSchema,
  sizeBytes: z.number().int().positive(),
  storageKey: storageKeySchema,
  checksumSha256: checksumSha256Schema.nullable(),
  uploadedByUserId: z.string().uuid(),
  archivedAt: z.string().datetime().nullable(),
  archivedByUserId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const companyFileCreateRequestSchema = z
  .object({
    kind: companyFileKindSchema,
    originalFilename: filenameSchema,
    mimeType: mimeTypeSchema,
    sizeBytes: z.number().int().positive().max(50 * 1024 * 1024),
    storageKey: storageKeySchema,
    checksumSha256: checksumSha256Schema.optional(),
  })
  .strict()
  .superRefine((input, context) => {
    if (input.kind === "logo" && !input.mimeType.startsWith("image/")) {
      context.addIssue({
        code: "custom",
        path: ["mimeType"],
        message: "Logo must be an image file",
      });
    }
  });

export const companyFileListQuerySchema = z.object({
  kind: companyFileKindSchema.optional(),
  includeArchived: z.coerce.boolean().default(false),
});

export const companyFileListResponseSchema = z.object({
  items: z.array(companyFileSchema),
  total: z.number().int().nonnegative(),
});

export const companyInternalNoteSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  body: z.string().trim().min(1),
  createdByUserId: z.string().uuid(),
  updatedByUserId: z.string().uuid().nullable(),
  archivedAt: z.string().datetime().nullable(),
  archivedByUserId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const companyInternalNoteCreateRequestSchema = z
  .object({ body: z.string().trim().min(1).max(10_000) })
  .strict();

export const companyInternalNoteUpdateRequestSchema = companyInternalNoteCreateRequestSchema;

export const companyInternalNoteListQuerySchema = z.object({
  includeArchived: z.coerce.boolean().default(false),
});

export const companyInternalNoteListResponseSchema = z.object({
  items: z.array(companyInternalNoteSchema),
  total: z.number().int().nonnegative(),
});

export type CompanyFileKind = z.infer<typeof companyFileKindSchema>;
export type CompanyFile = z.infer<typeof companyFileSchema>;
export type CompanyFileCreateRequest = z.infer<typeof companyFileCreateRequestSchema>;
export type CompanyFileListQuery = z.infer<typeof companyFileListQuerySchema>;
export type CompanyFileListResponse = z.infer<typeof companyFileListResponseSchema>;
export type CompanyInternalNote = z.infer<typeof companyInternalNoteSchema>;
export type CompanyInternalNoteCreateRequest = z.infer<typeof companyInternalNoteCreateRequestSchema>;
export type CompanyInternalNoteUpdateRequest = z.infer<typeof companyInternalNoteUpdateRequestSchema>;
export type CompanyInternalNoteListQuery = z.infer<typeof companyInternalNoteListQuerySchema>;
export type CompanyInternalNoteListResponse = z.infer<typeof companyInternalNoteListResponseSchema>;
